import { IAttributeDefinitionEntity } from '../../types/entities/IAttributeDefinitionEntity';
import { IAttributeValue } from '../../types/entities/IAttributeValue';
import { ISupplierHotel } from '../../types/entities/ISupplierHotel';
import { ISupplierHotelRoomType } from '../../types/entities/ISupplierHotelRoomType';
import {
    AttributeDefinitionFieldType,
    AttributeDefinitionSizeKeyType,
    ProgressArrayItem,
    ProgressState,
    PropertyState,
    RoomState,
    AttributeDefinitionState,
} from '../../types/properties/propertyTypes';
import { convertDateToUTC, isMinValueDate } from '../../utils/formatDates';
import {
    getProgressStateFromDB,
    tabsData,
} from '../../utils/property/propertyUtils';
import {
    getDefaultValue,
    getFieldType,
} from '../../utils/property/propertyZinantisUtils';
import { getRandomNumber } from '../../utils/usefulFunctions';

export const adaptDDBBAttributeDefinitionToState = (
    attributeDefinition: Partial<IAttributeDefinitionEntity>
): AttributeDefinitionState => {
    return {
        id: attributeDefinition.Id || '',
        idAttributeDefinition: attributeDefinition?.IdAttributeDefinition || 0,
        required: attributeDefinition.Required || false,
        fieldType: attributeDefinition?.AttributeDefinition?.FieldType
            ? getFieldType(attributeDefinition?.AttributeDefinition?.FieldType)
            : AttributeDefinitionFieldType.STRING,
        name: attributeDefinition.AttributeDefinition?.Name || '',
        unique: attributeDefinition.AttributeDefinition?.MustBeUnique || false,
        sizeKey:
            (attributeDefinition.AttributeDefinition
                ?.AttributeDefinitionSizeType
                ?.Key as AttributeDefinitionSizeKeyType) ||
            AttributeDefinitionSizeKeyType.SMALL,
        size:
            attributeDefinition.AttributeDefinition?.AttributeDefinitionSizeType
                ?.Size || 0,
        idCategory:
            attributeDefinition.AttributeDefinition
                ?.IdAttributeDefinitionCategory || 0,
        category:
            attributeDefinition.AttributeDefinition?.AttributeDefinitionCategory
                .LocalizedCaption ||
            attributeDefinition.AttributeDefinition?.AttributeDefinitionCategory
                .Caption ||
            attributeDefinition.AttributeDefinition?.AttributeDefinitionCategory
                .Name ||
            '',
    };
};

export const adaptDDBBAttributeValuesToState = (
    defValues: IAttributeValue[]
) => {
    return defValues.map(defValue => ({
        id: defValue.Id,
        idAttributeDefinition: defValue.IdAttributeDefinition,
        value: defValue.Value || '',
    }));
};

export const adaptDDBBAttributeValuesAndCreateNewOnesToState = (
    defValues: IAttributeValue[],
    definitions: IAttributeDefinitionEntity[]
) => {
    const adaptedValues = adaptDDBBAttributeValuesToState(defValues);
    const adaptedDefinitions = definitions.map(def =>
        adaptDDBBAttributeDefinitionToState(def)
    );

    const newValues = adaptedDefinitions
        .filter(definition => {
            const hasValue = adaptedValues.some(
                value =>
                    value.idAttributeDefinition ===
                    definition.idAttributeDefinition
            );
            return !hasValue;
        })
        .map(definition => ({
            id: `-${getRandomNumber()}`,
            idAttributeDefinition: definition.idAttributeDefinition,
            value: getDefaultValue(definition.fieldType),
            __isAdded: true,
            __isChanged: true,
        }));
    return [...adaptedValues, ...newValues];
};

export const adaptDDBBToPropertyState = (
    supplierHotel: ISupplierHotel
): PropertyState => {
    return {
        id: supplierHotel.Id || '',
        name: supplierHotel.Name || '',
        primary:
            supplierHotel.Hotel.SupplierHotels.find(
                supHotel =>
                    supHotel.IdSupplierHotel === parseInt(supplierHotel.Id)
            )?.Primary || false,
        idHotel: supplierHotel.IdHotel || 0,
        idSupplier: supplierHotel.IdSupplier || 0,
        idHotelCategory: supplierHotel.Hotel.IdHotelCategory || 0,
        idHotelChain: supplierHotel.Hotel.IdHotelChain || 0,
        roomCount: supplierHotel.Hotel.Rooms || 0,

        hotelCategory:
            supplierHotel.Hotel.HotelCategory.LocalizedCaption ||
            supplierHotel.Hotel.HotelCategory.Caption ||
            supplierHotel.Hotel.HotelCategory.Name ||
            '',
        rating: supplierHotel.Hotel.HotelCategory.RatingNumber || 0,
        idHotelType: supplierHotel.Hotel.IdHotelType || 0,
        idCurrency: supplierHotel.Hotel.IdCurrency || 0,
        checkInHour: supplierHotel.Hotel.CheckInHour || '',
        checkOutHour: supplierHotel.Hotel.CheckOutHour || '',
        address: supplierHotel.Hotel.Address || '',
        email: supplierHotel.Hotel.GeneralEmail || '',
        phone: supplierHotel.Hotel.GeneralPhone || '',
        bookingPhone: supplierHotel.Hotel.BookingPhone || '',
        bookingEmail: supplierHotel.Hotel.BookingEmail || '',
        accountingPhone: supplierHotel.Hotel.AccountingPhone || '',
        accountingEmail: supplierHotel.Hotel.AccountingEmail || '',
        idCountry: supplierHotel.Hotel.IdCountry || 0,
        idRegion: supplierHotel.Hotel.IdRegion || 0,
        idProvince: supplierHotel.Hotel.IdProvince || 0,
        idCity: supplierHotel.Hotel.IdCity || 0,
        postalCode: supplierHotel.Hotel.PostalCode || '',
        idAirportCode: supplierHotel.Hotel.HotelCodes.Id || '',
        airportCode: supplierHotel.Hotel.HotelCodes.NearbyAirportCode || '',
        idTimeZone: supplierHotel.Hotel.IdTimeZoneUtil || 0,
        adultsOnly: supplierHotel.Hotel.AdultsOnly || false,
        adultAge: supplierHotel.Hotel.AdultAge || 0,
        childrenAgeSegments:
            supplierHotel?.ChildrenAgeSegments?.map(segment => ({
                id: segment.Id,
                age: segment.Age || 0,
                name:
                    segment?.LocalizedCaption ||
                    segment?.Caption ||
                    segment?.Name ||
                    '',
                key: segment.Key || '',
                isBaby: segment.IsBaby || false,
                isFirstChild: segment.IsFirstChild || false,
                isOtherChild: segment.IsOtherChild || false,
                isSecondChild: segment.IsSecondChild || false,
            })) || [],
        descriptions: supplierHotel.Descriptions.filter(
            description => description.DescriptionType.Key === 'LONG'
        ).map(description => ({
            id: description.Id,
            idLanguage: description.IdContentLanguage || 0,
            languageCode: description.ContentLanguage.CultureId || '',
            language:
                description.ContentLanguage?.LocalizedCaption ||
                description.ContentLanguage?.Caption ||
                description.ContentLanguage?.Name ||
                '',
            descriptionContent: description.Description || '',
        })),
        services: supplierHotel.Services.map(service => ({
            id: service.Id,
            name:
                service?.LocalizedCaption ||
                service?.Caption ||
                service?.Name ||
                '',
            free: service.Free || false,
        })),
        zitoneoServices: supplierHotel.Hotel.HotelServiceTypes.filter(
            service =>
                service.Enabled &&
                service.ServiceType.Enabled &&
                service.ServiceType.ServiceCategory.Enabled
        ).map(zitoneoService => ({
            id: zitoneoService.Id,
            serviceType:
                zitoneoService?.ServiceType?.LocalizedCaption ||
                zitoneoService?.ServiceType?.Caption ||
                zitoneoService?.ServiceType?.Name ||
                '',
            free: zitoneoService.ServiceType?.Free || false,
            idServiceType: zitoneoService.IdServiceType || 0,
            idServiceCategory:
                zitoneoService.ServiceType?.IdServiceCategory || 0,
            serviceCategory:
                zitoneoService.ServiceType?.ServiceCategory?.LocalizedCaption ||
                zitoneoService.ServiceType?.ServiceCategory?.Caption ||
                zitoneoService.ServiceType?.ServiceCategory?.Name ||
                '',
            attributeDefinitions:
                zitoneoService.AttributeDefinitionEntities.map(definition =>
                    adaptDDBBAttributeDefinitionToState(definition)
                ),
            attributeValues: adaptDDBBAttributeValuesAndCreateNewOnesToState(
                zitoneoService.AttributeDefinitionValues,
                zitoneoService.AttributeDefinitionEntities
            ),
        })),
        attributeDefinitions:
            supplierHotel.Hotel.AttributeDefinitionEntities.map(definition =>
                adaptDDBBAttributeDefinitionToState(definition)
            ),
        attributeValues: adaptDDBBAttributeValuesAndCreateNewOnesToState(
            supplierHotel.Hotel.AttributeDefinitionValues,
            supplierHotel.Hotel.AttributeDefinitionEntities
        ),
        hostSegments: supplierHotel.HostSegments.map(segment => ({
            id: segment.Id,
            name:
                segment?.LocalizedCaption ||
                segment?.Caption ||
                segment?.Name ||
                '',
        })),
        mealPlanTypes: supplierHotel.SupplierHotelMealPlans.filter(
            mealPlan => mealPlan.Enabled
        ).map(mealPlan => ({
            id: mealPlan.Id,
            name: mealPlan.Name || '',
            mappingCode: mealPlan.MappingCode || '',
            idType: parseInt(mealPlan.MealPlanType.Id) || 0,
            typeName:
                mealPlan.MealPlanType?.LocalizedCaption ||
                mealPlan.MealPlanType?.Caption ||
                mealPlan.MealPlanType?.Name ||
                '',
            typeMappingCodeTemplate:
                mealPlan.MealPlanType.MappingCodeTemplate || '',
        })),
        images: supplierHotel.SupplierHotelImages.map(image => ({
            id: image.Id,
            caption: image.Caption || '',
            feature: image.Feature || false,
            is360: image.Is360 || false,
            published: image.Published || false,
            idType: image.IdHotelImageType || 0,
            type: image.HotelImageType.Key || '',
            url: image.VirtualPath || '',
            idRoom: image.IdSupplierHotelRoomType || 0,
        })),
        rooms: supplierHotel.SupplierHotelRoomTypes.sort(
            (a, b) =>
                new Date(b.DateLastUpdated).getTime() -
                new Date(a.DateLastUpdated).getTime()
        ).map(room => adaptRoomDDBBToState(room)),
        notes: supplierHotel.SpecialNotes.sort(
            (a, b) =>
                new Date(b.DateLastUpdated).getTime() -
                new Date(a.DateLastUpdated).getTime()
        ).map(note => ({
            id: note.Id,
            message: note.Message || '',
            fromBookingDate: note.FromBookingDate || false,
            fromDate: note.FromDate || null,
            toDate: note.ToDate || null,
            enabled: note.Enabled || false,
        })),
        taxes: supplierHotel.SupplierTaxes.sort(
            (a, b) =>
                new Date(b.DateLastUpdated).getTime() -
                new Date(a.DateLastUpdated).getTime()
        ).map(tax => ({
            id: tax.Id,
            name: tax.Name || '',
            enabled: tax.Enabled || false,
            description: tax.Description || '',
            idRoom: tax.IdSupplierHotelRoomType || 0,
            room: tax.SupplierHotelRoomType?.Name || '',
            idMealPlan: tax.IdSupplierHotelMealPlan || 0,
            mealPlan: tax.SupplierHotelMealPlan?.Name || '',
            idRatePlan: tax.IdSupplierHotelRatePlan || 0,
            ratePlan: tax.SupplierHotelRatePlan?.Name || '',
            idTaxType: tax.IdSupplierTaxType || 0,
            taxType:
                tax.SupplierTaxType?.Caption || tax.SupplierTaxType?.Name || '',
            taxTypeKey: tax.SupplierTaxType?.Key || '',
            idTaxCalculationType: tax.IdSupplierTaxCalculationType || 0,
            taxCalculationType:
                tax.SupplierTaxCalculationType?.LocalizedCaption ||
                tax.SupplierTaxCalculationType?.Caption ||
                tax.SupplierTaxCalculationType?.Name ||
                '',
            taxCalculationTypeKey: tax.SupplierTaxCalculationType?.Key || '',
            idTaxCalculationRuleType: tax.IdSupplierTaxCalculationRuleType || 0,
            taxCalculationRuleType:
                tax.SupplierTaxCalculationRuleType?.LocalizedCaption ||
                tax.SupplierTaxCalculationRuleType?.Caption ||
                tax.SupplierTaxCalculationRuleType?.Name ||
                '',
            taxCalculationRuleTypeKey:
                tax.SupplierTaxCalculationRuleType?.Key || '',
            fromDate: isMinValueDate(tax.FromDate) ? null : tax.FromDate,
            toDate: isMinValueDate(tax.ToDate) ? null : tax.ToDate,
        })),
        supplements: supplierHotel.SupplierSupplements.sort(
            (a, b) =>
                new Date(b.DateLastUpdated).getTime() -
                new Date(a.DateLastUpdated).getTime()
        ).map(supplement => ({
            id: supplement.Id,
            name: supplement.Name || '',
            enabled: supplement.Enabled || false,
            description: supplement.Description || '',
            idRoom: supplement.IdSupplierHotelRoomType || 0,
            room: supplement.SupplierHotelRoomType?.Name || '',
            idMealPlan: supplement.IdSupplierHotelMealPlan || 0,
            mealPlan: supplement.SupplierHotelMealPlan?.Name || '',
            idRatePlan: supplement.IdSupplierHotelRatePlan || 0,
            ratePlan: supplement.SupplierHotelRatePlan?.Name || '',
            idTaxCalculationType: supplement.IdSupplierTaxCalculationType || 0,
            taxCalculationType:
                supplement.SupplierTaxCalculationType?.LocalizedCaption ||
                supplement.SupplierTaxCalculationType?.Caption ||
                supplement.SupplierTaxCalculationType?.Name ||
                '',
            taxCalculationTypeKey:
                supplement.SupplierTaxCalculationType?.Key || '',
            idTaxCalculationRuleType:
                supplement.IdSupplierTaxCalculationRuleType || 0,
            taxCalculationRuleType:
                supplement.SupplierTaxCalculationRuleType?.LocalizedCaption ||
                supplement.SupplierTaxCalculationRuleType?.Caption ||
                supplement.SupplierTaxCalculationRuleType?.Name ||
                '',
            taxCalculationRuleTypeKey:
                supplement.SupplierTaxCalculationRuleType?.Key || '',
            fromDate: isMinValueDate(supplement.FromDate)
                ? null
                : supplement.FromDate,
            toDate: isMinValueDate(supplement.ToDate)
                ? null
                : supplement.ToDate,
        })),
        stopSales: supplierHotel.SupplierHotelStopSales.map(stopSale => ({
            id: stopSale.Id,
            fromDate: isMinValueDate(stopSale.FromDate)
                ? null
                : stopSale.FromDate,
            toDate: isMinValueDate(stopSale.ToDate) ? null : stopSale.ToDate,
            enabled: stopSale.Enabled || false,
            idRoom: stopSale.IdSupplierHotelRoomType || 0,
            room: stopSale.SupplierHotelRoomType?.Name || '',
            idMealPlan: stopSale.IdSupplierHotelMealPlan || 0,
            mealPlan: stopSale.SupplierHotelMealPlan?.Name || '',
            idRatePlan: stopSale.IdSupplierHotelRatePlan || 0,
            ratePlan: stopSale.SupplierHotelRatePlan?.Name || '',
        })),
    };
};

export const adaptDDBBToProgressState = (
    supplierHotel: ISupplierHotel
): Partial<ProgressState> => {
    const progressArray: ProgressArrayItem[] =
        supplierHotel.SupplierHotelContentWizardProgress.map(progress => ({
            id: progress.Id,
            step: progress.Step || '',
            tab: progress.Tab || '',
        }));

    if (supplierHotel.Progress >= 95) {
        Object.entries(tabsData).forEach(([stepKey, stepData]) => {
            stepData.tabs.forEach(tab => {
                const exists = progressArray.some(
                    p => p.step === stepKey && p.tab === tab.title
                );
                if (!exists) {
                    progressArray.push({
                        id: `-${getRandomNumber()}`,
                        step: stepKey,
                        tab: tab.title,
                        __isAdded: true,
                        __isChanged: true,
                    });
                }
            });
        });
    }
    return {
        totalProgress: supplierHotel.Progress || 0,
        progressByStep: getProgressStateFromDB(progressArray),
        progressArray,
    };
};

export const adaptPropertyStateToDDBB = (
    propertyState: PropertyState,
    progress: ProgressState
) => {
    return {
        Id: propertyState.id,
        Name: propertyState.name,
        Progress: progress.totalProgress,
        SupplierHotelContentWizardProgress: progress.progressArray.map(
            progressItem => ({
                Id: progressItem.id,
                Step: progressItem.step || '',
                Tab: progressItem.tab || '',
                Completed: true,
                __isAdded: progressItem.__isAdded || false,
                __isChanged:
                    progressItem.__isChanged || progressItem.__isAdded || false,
            })
        ),
        Hotel: {
            IdHotelType: propertyState.idHotelType,
            IdHotelCategory: propertyState.idHotelCategory,
            IdHotelChain: propertyState.idHotelChain,
            IdCurrency: propertyState.idCurrency,
            IdCity: propertyState.idCity,
            IdProvince: propertyState.idProvince,
            IdRegion: propertyState.idRegion,
            IdCountry: propertyState.idCountry,
            IdTimeZoneUtil: propertyState.idTimeZone,
            GeneralPhone: propertyState.phone,
            GeneralEmail: propertyState.email,
            BookingPhone: propertyState.bookingPhone,
            BookingEmail: propertyState.bookingEmail,
            AccountingPhone: propertyState.accountingPhone,
            AccountingEmail: propertyState.accountingEmail,
            Address: propertyState.address,
            CheckInHour: propertyState.checkInHour,
            CheckOutHour: propertyState.checkOutHour,
            PostalCode: propertyState.postalCode,
            HotelCodes: {
                Id: propertyState.idAirportCode,
                NearbyAirportCode: propertyState.airportCode,
            },
            Rooms: propertyState.roomCount,
            AdultsOnly: propertyState.adultsOnly,
            AdultAge: propertyState.adultAge,
            HotelServiceTypes: propertyState.zitoneoServices.map(service => ({
                Id: service.id,
                IdServiceType: service.idServiceType,
                AttributeDefinitionValues: service.attributeValues
                    ?.filter(attrValue => {
                        const isNewValue =
                            parseInt(attrValue.id) < 0 && !!attrValue.__isAdded;
                        const hasValue =
                            typeof attrValue.value === 'boolean'
                                ? true
                                : attrValue.value !== null &&
                                  attrValue.value !== undefined &&
                                  String(attrValue.value).trim() !== '';

                        return !(isNewValue && !hasValue);
                    })
                    .map(attrValue => {
                        return {
                            Id: attrValue.id,
                            IdAttributeDefinition:
                                attrValue.idAttributeDefinition,
                            Value: attrValue.value,
                            __isAdded: !!attrValue.__isAdded,
                            __isChanged:
                                !!attrValue.__isAdded ||
                                !!attrValue.__isChanged,
                            Deleted: !!attrValue.Deleted,
                        };
                    }),
                __isAdded: !!service.__isAdded,
                __isChanged: !!service.__isAdded || !!service.__isChanged,
                Deleted: !!service.Deleted,
            })),
            AttributeDefinitionValues: propertyState.attributeValues
                ?.filter(attrValue => {
                    const isNewValue =
                        parseInt(attrValue.id) < 0 && !!attrValue.__isAdded;
                    const hasValue =
                        typeof attrValue.value === 'boolean'
                            ? true
                            : attrValue.value !== null &&
                              attrValue.value !== undefined &&
                              String(attrValue.value).trim() !== '';

                    return !(isNewValue && !hasValue);
                })
                .map(attrValue => {
                    return {
                        Id: attrValue.id,
                        IdAttributeDefinition: attrValue.idAttributeDefinition,
                        Value: attrValue.value,
                        __isAdded: !!attrValue.__isAdded,
                        __isChanged:
                            !!attrValue.__isAdded || !!attrValue.__isChanged,
                        Deleted: !!attrValue.Deleted,
                    };
                }),
        },
        HostSegments: propertyState.hostSegments.map(segment => ({
            Id: segment.id,
            __isAdded: segment.__isAdded || false,
            Deleted: segment.Deleted || false,
        })),
        Services: propertyState.services.map(service => ({
            Id: service.id,
            __isAdded: service.__isAdded || false,
            Deleted: service.Deleted || false,
        })),
        SupplierHotelMealPlans: propertyState.mealPlanTypes.map(mealPlan => ({
            Id: mealPlan.id,
            Name: mealPlan.name,
            MappingCode: mealPlan.mappingCode,
            IdMealPlanType: mealPlan.idType,
            Enabled: true,
            __isAdded: mealPlan.__isAdded || false,
            __isChanged: mealPlan.__isChanged || false,
            Deleted: mealPlan.Deleted || false,
        })),
        Descriptions: propertyState.descriptions.map(description => ({
            Id: description.id,
            IdContentLanguage: description.idLanguage,
            IdDescriptionType: 2, //LONG description type
            Description: description.descriptionContent,
            IdSupplierHotel: parseInt(propertyState.id),
            IdSupplier: propertyState.idSupplier,
            __isAdded: description.__isAdded || false,
            __isChanged:
                description.__isChanged || description.__isAdded || false,
            Deleted: description.Deleted || false,
        })),
        SupplierHotelRoomTypes: propertyState.rooms.map(room => ({
            Id: room.id,
            Name: room.name,
            AdultOnly: room.adultsOnly,
            FreeBaby1: room.freeBaby1,
            FreeBaby2: room.freeBaby2,
            Occupancy: room.occupancy,
            StandardOccupancy: room.standardOccupancy,
            MinAdults: room.minAdults,
            MaxAdults: room.maxAdults,
            MinChildren: room.minChildren,
            MaxChild: room.maxChildren,
            Size: room.size,
            Quantity: room.quantity,
            Services: room.services.map(service => ({
                Id: service.id,
                __isAdded: service.__isAdded || false,
                Deleted: service.Deleted || false,
            })),
            SupplierHotelRoomBedTypes: room.bedTypes.map(bedType => ({
                Id: bedType.id,
                __isAdded: bedType.__isAdded || false,
                __isChanged: bedType.__isChanged || bedType.__isAdded || false,
                Deleted: bedType.Deleted || false,
            })),
            SupplierHotelRoomTypesImages: room.images.map(image => ({
                Id: image.id,
                Caption: image.caption,
                Feature: image.feature,
                Is360: image.is360,
                Published: image.published,
                IdHotelImageType: image.idType,
                IdSupplierHotelRoomType: image.idRoom,
                __isAdded: image.__isAdded || false,
                __isChanged: image.__isChanged || image.__isAdded || false,
                Deleted: image.Deleted || false,
            })),
            SupplierHotelRoomTypeDescriptions: room.descriptions.map(
                description => {
                    return {
                        Id: description.id,
                        IdContentLanguage: description.idLanguage,
                        IdDescriptionType: 2, //LONG description type
                        Description: description.descriptionContent,
                        IdSupplierHotelRoomType: parseInt(room.id),
                    };
                }
            ),
            __isAdded: room.__isAdded || false,
            __isChanged: room.__isChanged || false,
        })),
        SpecialNotes: propertyState.notes.map(note => ({
            Id: note.id,
            FromDate: note.fromDate ? convertDateToUTC(note.fromDate) : null,
            ToDate: note.toDate ? convertDateToUTC(note.toDate) : null,
            FromBookingDate: note.fromBookingDate,
            Message: note.message,
            Enabled: note.enabled,
            __isAdded: note.__isAdded || false,
            __isChanged: note.__isChanged || note.__isAdded || false,
            Deleted: note.Deleted || false,
        })),
        SupplierHotelImages: propertyState.images.map(image => {
            return {
                Id: image.id,
                Caption: image.caption,
                Feature: image.feature,
                Is360: image.is360,
                Published: image.published,
                IdHotelImageType: image.idType,
                IdSupplierHotelRoomType: image.idRoom,
                __isAdded: image.__isAdded || false,
                __isChanged: image.__isChanged || image.__isAdded || false,
                Deleted: image.Deleted || false,
            };
        }),
        ChildrenAgeSegments: propertyState.childrenAgeSegments.map(segment => ({
            Id: segment.id,
            Age: segment.age || 0,
            IsBaby: segment.isBaby || false,
            IsFirstChild: segment.isFirstChild || false,
            IsOtherChild: segment.isOtherChild || false,
            IsSecondChild: segment.isSecondChild || false,
            __isAdded: segment.__isAdded || false,
            __isChanged: segment.__isChanged || segment.__isAdded || false,
            Deleted: segment.Deleted || false,
        })),
        // necesario para que se grabe bien
        DateLastUpdated: new Date().toISOString(),
    };
};

export const adaptRoomDDBBToState = (
    room: ISupplierHotelRoomType
): RoomState => {
    return {
        id: room.Id,
        name: room.Name || '',
        adultsOnly: room.AdultOnly || false,
        freeBaby1: room.FreeBaby1 || false,
        freeBaby2: room.FreeBaby2 || false,
        occupancy: room.Occupancy || '',
        standardOccupancy: room.StandardOccupancy || 0,
        minAdults: room.MinAdults || 0,
        maxAdults: room.MaxAdults || 0,
        minChildren: room.MinChildren || 0,
        maxChildren: room.MaxChild || 0,
        size: room.Size || 0,
        quantity: room.Quantity || 0,
        images: room.SupplierHotelRoomTypesImages.map(image => ({
            id: image.Id,
            caption: image.Caption || '',
            feature: image.Feature || false,
            is360: image.Is360 || false,
            published: image.Published || false,
            type: image.HotelImageType.Key || '',
            idType: image.IdHotelImageType || 0,
            url: image.VirtualPath || '',
            idRoom: image.IdSupplierHotelRoomType || 0,
        })),
        services: room.Services.map(service => ({
            id: service.Id,
            name:
                service?.LocalizedCaption ||
                service?.Caption ||
                service?.Name ||
                '',
            free: service.Free || false,
        })),
        bedTypes: room.SupplierHotelRoomBedTypes.map(bedType => ({
            id: bedType.Id,
            name:
                bedType?.LocalizedCaption ||
                bedType?.Caption ||
                bedType?.Name ||
                '',
        })),
        descriptions: room.SupplierHotelRoomTypeDescriptions.filter(
            description => description.DescriptionType.Key === 'LONG'
        ).map(description => {
            return {
                id: description.Id,
                idLanguage: description.IdContentLanguage,
                languageCode: description.ContentLanguage.CultureId,
                language:
                    description.ContentLanguage?.LocalizedCaption ||
                    description.ContentLanguage?.Caption ||
                    description.ContentLanguage?.Name ||
                    '',
                descriptionContent: description.Description,
            };
        }),
    };
};

export const adaptRoomToDDBB = (room: RoomState) => {
    return {
        Id: room.id,
        Name: room.name,
        AdultOnly: room.adultsOnly,
        FreeBaby1: room.freeBaby1,
        FreeBaby2: room.freeBaby2,
        Occupancy: room.occupancy,
        StandardOccupancy: room.standardOccupancy,
        MinAdults: room.minAdults,
        MaxAdults: room.maxAdults,
        MinChildren: room.minChildren,
        MaxChild: room.maxChildren,
        Size: room.size,
        Quantity: room.quantity,
        Services: room.services.map(service => ({
            Id: service.id,
            __isAdded: service.__isAdded || false,
            Deleted: service.Deleted || false,
        })),
        SupplierHotelRoomBedTypes: room.bedTypes.map(bedType => ({
            Id: bedType.id,
            __isAdded: bedType.__isAdded || false,
            __isChanged: bedType.__isChanged || bedType.__isAdded || false,
            Deleted: bedType.Deleted || false,
        })),
        SupplierHotelRoomTypesImages: room.images.map(image => ({
            Id: image.id,
            Caption: image.caption,
            Feature: image.feature,
            Is360: image.is360,
            Published: image.published,
            IdHotelImageType: image.idType,
            IdSupplierHotelRoomType: image.idRoom,
            __isAdded: image.__isAdded || false,
            __isChanged: image.__isChanged || image.__isAdded || false,
            Deleted: image.Deleted || false,
        })),
        SupplierHotelRoomTypeDescriptions: room.descriptions.map(
            description => {
                const newDesc = parseInt(description.id) <= 0;
                return {
                    Id: description.id,
                    IdContentLanguage: description.idLanguage,
                    IdDescriptionType: 2, //LONG description type
                    Description: description.descriptionContent,
                    __isAdded: !!newDesc,
                    __isChanged: !!newDesc || description.__isChanged || false,
                    Deleted: description.Deleted || false,
                };
            }
        ),
        __isAdded: room.__isAdded || false,
        __isChanged: room.__isChanged || false,
    };
};
